import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import { couponApiService } from "services/couponApiService";

const CreateCoupon = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pro_name: "",
    promocode: "",
    promo_percentage: "",
    promo_fixedamount: "",
    is_fixedamount: false,
    is_freedelivery: false,
    is_percentage: false,
    activestatus: true,
  });

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'promo_percentage' || field === 'promo_fixedamount'
        ? (value === '' ? '' : parseFloat(value))
        : value
    }));
  };

  // Handle boolean select changes
  const handleBooleanChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value === 'true'
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!formData.pro_name.trim()) {
      toast.error("Promo name is required");
      return false;
    }
    if (!formData.promocode.trim()) {
      toast.error("Promo code is required");
      return false;
    }

    // Validate at least one discount type is selected
    if (!formData.is_freedelivery && !formData.is_percentage && !formData.is_fixedamount) {
      toast.error("Please select at least one discount type (Percentage or Fixed Amount)");
      return false;
    }

    // Validate percentage value if percentage is selected
    if (!formData.is_freedelivery && formData.is_percentage && (!formData.promo_percentage || formData.promo_percentage <= 0 || formData.promo_percentage > 100)) {
      toast.error("Please enter a valid percentage between 1 and 100");
      return false;
    }

    // Validate fixed amount value if fixed amount is selected
    if (formData.is_fixedamount && (!formData.promo_fixedamount || formData.promo_fixedamount <= 0)) {
      toast.error("Please enter a valid fixed amount");
      return false;
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    console.log("object", "not work");
    setLoading(true);

    try {
      // Prepare data for API
      const couponData = {
        pro_name: formData.pro_name,
        promocode: formData.promocode.toUpperCase(), // Convert to uppercase
        promo_percentage: formData.is_percentage ? formData.promo_percentage : "",
        promo_fixedamount: formData.is_fixedamount ? formData.promo_fixedamount : "",
        is_fixedamount: formData.is_fixedamount,
        is_freedelivery: formData.is_freedelivery,
        is_percentage: formData.is_percentage,
        activestatus: formData.activestatus,
      };

      await couponApiService.createCoupon(couponData);

      toast.success("Coupon created successfully!");

      // Navigate to coupons list
      setTimeout(() => {
        navigate("/all-coupons");
      }, 1500);

    } catch (error) {
      console.error("Error creating coupon:", error);

      if (error.response?.status === 409) {
        toast.error("This promo code already exists. Please use a different code.");
      } else {
        toast.error("Failed to create coupon. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      pro_name: "",
      promocode: "",
      promo_percentage: "",
      promo_fixedamount: "",
      is_fixedamount: false,
      is_freedelivery: false,
      is_percentage: false,
      activestatus: true,
    });
  };

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Coupons" breadcrumbItem="Create New Coupon" />

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h4 className="card-title mb-4">Create New Coupon</h4>

                <form onSubmit={handleSubmit}>
                  {/* Basic Information */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Promo Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.pro_name}
                          onChange={(e) => handleInputChange('pro_name', e.target.value)}
                          required
                          placeholder="Enter promo name"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Promo Code <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.promocode}
                          onChange={(e) => handleInputChange('promocode', e.target.value)}
                          required
                          placeholder="Enter promo code (e.g., SAVE20)"
                          style={{ textTransform: 'uppercase' }}
                        />
                        <small className="text-muted">Will be automatically converted to uppercase</small>
                      </div>
                    </div>
                  </div>

                  {/* Discount Type Selection */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <h6 className="mb-3">Discount Type <span className="text-danger">*</span></h6>

                      <div className="form-check form-check-inline me-3">
                        <input
                          className="form-check-input"
                          style={{ border: "1px solid silver" }}
                          type="checkbox"
                          id="is_percentage"
                          checked={formData.is_percentage}
                          onChange={(e) => {
                            handleInputChange('is_percentage', e.target.checked);
                            if (e.target.checked) {
                              handleInputChange('is_fixedamount', false);
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="is_percentage">
                          Percentage Discount
                        </label>
                      </div>

                      <div className="form-check form-check-inline">
                        <input
                          className="form-check-input"
                          style={{ border: "1px solid silver" }}
                          type="checkbox"
                          id="is_fixedamount"
                          checked={formData.is_fixedamount}
                          onChange={(e) => {
                            handleInputChange('is_fixedamount', e.target.checked);
                            if (e.target.checked) {
                              handleInputChange('is_percentage', false);
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="is_fixedamount">
                          Fixed Amount Discount
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            style={{ border: "1px solid silver" }}
                            type="checkbox"
                            id="is_freedelivery"
                            checked={formData.is_freedelivery}
                            onChange={(e) => handleInputChange('is_freedelivery', e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="is_freedelivery">
                            Free Delivery
                          </label>
                          <small className="text-muted d-block">Enable free delivery with this coupon</small>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Discount Values */}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Promo Percentage
                          {formData.is_percentage && <span className="text-danger"> *</span>}
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={formData.promo_percentage}
                            onChange={(e) => handleInputChange('promo_percentage', e.target.value)}
                            placeholder="Enter percentage"
                            disabled={!formData.is_percentage}
                            min="1"
                            max="100"
                          />
                          <span className="input-group-text">%</span>
                        </div>
                        {formData.is_percentage && (
                          <small className="text-muted">Enter percentage between 1-100</small>
                        )}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">
                          Promo Fixed Amount
                          {formData.is_fixedamount && <span className="text-danger"> *</span>}
                        </label>
                        <div className="input-group">
                          <span className="input-group-text">৳</span>
                          <input
                            type="number"
                            className="form-control"
                            value={formData.promo_fixedamount}
                            onChange={(e) => handleInputChange('promo_fixedamount', e.target.value)}
                            placeholder="Enter fixed amount"
                            disabled={!formData.is_fixedamount}
                            min="1"
                          />
                        </div>
                        {formData.is_fixedamount && (
                          <small className="text-muted">Enter fixed discount amount</small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Options */}
                  <div className="row mb-3">


                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={formData.activestatus}
                          onChange={(e) => handleBooleanChange('activestatus', e.target.value)}
                        >
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                        <small className="text-muted">Set coupon as active or inactive</small>
                      </div>
                    </div>
                  </div>



                  {/* Form Actions */}
                  <div className="row">
                    <div className="col-12">
                      <div className="d-flex justify-content-between">


                        <div className="d-flex gap-2">
                          {/* <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={handleReset}
                            disabled={loading}
                          >
                            <i className="fas fa-redo me-1"></i>
                            Reset
                          </button> */}

                          <button
                            type="submit"
                            className="btn btn-success"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <i className="fas fa-spinner fa-spin me-1"></i>
                                Creating...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-plus-circle me-1"></i>
                                Create Coupon
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCoupon;